import React from "react";
import { Check } from "lucide-react";
import {
  SCROLL_OFFSET,
  BODY,
  LABEL,
  GRADIENT_TEXT,
  CONTAINER,
  CARD_SHADOW,
} from "./sections";

const COLUMNS = {
  feature: "Feature",
  recognition: "Recognition alone",
  aiVoiceTyping: "AI voice typing",
};

// `check: true` renders the value with the blue tick used for supported items.
const ROWS = [
  {
    feature: "Converts speech to text",
    recognition: { text: "Yes", check: true },
    ai: "Yes",
  },
  {
    feature: "Adds punctuation automatically",
    recognition: { text: "Rarely" },
    ai: "Yes",
  },
  {
    feature: "Understands context & meaning",
    recognition: { text: "No" },
    ai: "Yes",
  },
  {
    feature: "Formats paragraphs & lists",
    recognition: { text: "No" },
    ai: "Often",
  },
  {
    feature: "Offers custom writing styles",
    recognition: { text: "No" },
    ai: "Yes",
  },
  {
    feature: "Keeps raw, unformatted text",
    recognition: { text: "N/A" },
    ai: "Yes",
  },
  {
    feature: "Output ready to use as-is",
    recognition: { text: "Rarely" },
    ai: "Usually",
  },
];

// Every cell carries the same gutter so the vertical rules line up with the
// header rule above them.
const CELL_PAD = "px-[16px] lg:px-[21.46px]";
const HEAD_CELL = `align-bottom ${CELL_PAD} py-[17.17px] text-left ${LABEL}`;
const BODY_CELL = `align-top ${CELL_PAD} py-[17.17px] font-inter text-[14px] leading-[24px] tracking-[0px]`;

// Vertical rule closing off each column except the last.
const V_RULE = "border-r-[1.07px] border-[#E6ECF5]";

const TickValue = ({ children }) => (
  <span className="flex items-start gap-[7px] text-[#3AB1F6]">
    <Check
      className="mt-[5px] h-[13px] w-[13px] shrink-0"
      strokeWidth={2.5}
      aria-hidden="true"
    />
    <span>{children}</span>
  </span>
);

const RecognitionValue = ({ value }) =>
  value.check ? (
    <TickValue>{value.text}</TickValue>
  ) : (
    <span className="text-[#0A0D1699]">{value.text}</span>
  );

const TranscriptionStepSection = () => {
  return (
    <section
      id="transcription-step"
      style={{ scrollMarginTop: `${SCROLL_OFFSET}px` }}
      className="w-full"
    >
      <div
        className={`${CONTAINER} pt-[64px] pb-[64px] md:pt-[88px] md:pb-[88px] lg:pt-[112px] lg:pb-[112px]`}
      >
        {/*
          Each clause is its own block from sm up so the heading always breaks
          after "recognition". At the shared h2 sizes the first clause is wider
          than the column between sm and lg and would wrap again, turning the
          heading into three lines, so the size is fluid here: it tops out at
          the standard 50px on wide screens and shrinks just enough to keep both
          clauses on one line each. Below sm the clauses fall back to inline
          text and wrap naturally.
        */}
        <h2 className="mx-auto max-w-[1100px] text-center font-jakarta font-bold text-[#0A0D16] text-[clamp(28px,4.1vw,50px)] leading-[1.28] tracking-[-0.8px]">
          <span className="sm:block">
            Think of traditional speech recognition
          </span>{" "}
          <span className="sm:block">as the transcription step.</span>
        </h2>

        <p className={`${BODY} mx-auto mt-[25.76px] max-w-[560px] text-center`}>
          AI voice typing combines transcription with tools that help turn that
          transcription into usable writing.
        </p>

        <div className="pt-[30.05px]">
          {/*
            Below sm the three columns cannot hold their line length, so each
            feature becomes a stacked row instead of a table cell.
          */}
          <div
            className="overflow-hidden rounded-[17.17px] border-[1.34px] border-[#E6ECF5] bg-white sm:hidden"
            style={{ boxShadow: CARD_SHADOW }}
          >
            <div className="border-b-[1.34px] border-[#E6ECF5] bg-[#F7FAFF] px-[16px] py-[14px]">
              <p className={`${LABEL} text-[#0A0D16]`}>{COLUMNS.feature}</p>
            </div>

            {ROWS.map(({ feature, recognition, ai }, index) => (
              <div
                key={feature}
                className={`px-[16px] py-[16px] ${
                  index === ROWS.length - 1
                    ? ""
                    : "border-b-[1.07px] border-[#E6ECF5]"
                }`}
              >
                <p className="font-inter text-[14px] font-normal leading-[24px] text-[#0A0D16]">
                  {feature}
                </p>

                <div className="mt-[10px] flex flex-col gap-[8px]">
                  <div className="flex items-start justify-between gap-3">
                    <span className={`${LABEL} text-[#0A0D1699]`}>
                      {COLUMNS.recognition}
                    </span>
                    <span className="font-inter text-[14px] leading-[24px]">
                      <RecognitionValue value={recognition} />
                    </span>
                  </div>

                  <div className="flex items-start justify-between gap-3">
                    <span className={LABEL}>
                      <span className={GRADIENT_TEXT}>
                        {COLUMNS.aiVoiceTyping}
                      </span>
                    </span>
                    <span className="font-inter text-[14px] leading-[24px]">
                      <TickValue>{ai}</TickValue>
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Table - sm and up */}
          <div
            className="hidden overflow-hidden rounded-[17.17px] border-[1.34px] border-[#E6ECF5] bg-white sm:block"
            style={{ boxShadow: CARD_SHADOW }}
          >
            <table className="w-full table-fixed border-collapse">
              <colgroup>
                <col className="w-[46%]" />
                <col className="w-[30%]" />
                <col className="w-[24%]" />
              </colgroup>

              <thead>
                <tr className="bg-[#F7FAFF]">
                  <th
                    scope="col"
                    className={`${HEAD_CELL} ${V_RULE} border-b-[1.34px] border-b-[#E6ECF5] text-[#0A0D16]`}
                  >
                    {COLUMNS.feature}
                  </th>
                  <th
                    scope="col"
                    className={`${HEAD_CELL} ${V_RULE} border-b-[1.34px] border-b-[#E6ECF5] text-[#0A0D1699]`}
                  >
                    {COLUMNS.recognition}
                  </th>
                  <th
                    scope="col"
                    className={`${HEAD_CELL} border-b-[1.34px] border-b-[#E6ECF5]`}
                  >
                    <span className={GRADIENT_TEXT}>
                      {COLUMNS.aiVoiceTyping}
                    </span>
                  </th>
                </tr>
              </thead>

              <tbody>
                {ROWS.map(({ feature, recognition, ai }, index) => {
                  const divider =
                    index === ROWS.length - 1
                      ? ""
                      : "border-b-[1.07px] border-b-[#E6ECF5]";

                  return (
                    <tr key={feature}>
                      <th
                        scope="row"
                        className={`${BODY_CELL} ${V_RULE} ${divider} text-left font-normal text-[#0A0D16]`}
                      >
                        {feature}
                      </th>
                      <td
                        className={`${BODY_CELL} ${V_RULE} ${divider} font-normal`}
                      >
                        <RecognitionValue value={recognition} />
                      </td>
                      <td className={`${BODY_CELL} ${divider} font-normal`}>
                        <TickValue>{ai}</TickValue>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TranscriptionStepSection;
